import { RefreshCcw, Search } from "lucide-react";
import React from "react";
import { useDispatch } from "react-redux";

import {
  IgButton,
  IgCheckbox,
  IgIconButton,
  IgTextField,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { CombinedCustomEvents } from "@intelligems/ig-types";
import { Grid, InputAdornment, useTheme } from "@mui/material";
import { combinedCustomEventsKeys } from "@src/api/queryKeys";
import { UpgradePlanText } from "@src/components/LimitAccess/access-text";
import { useCanCreateCustomEvent } from "@src/components/LimitAccess/check-custom-events-access-hooks";
import { ScopedComponentWithDisable } from "@src/components/LimitAccess/ScopedComponentWithDisable";
import { openDrawer } from "@src/store/slices/drawers";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";

interface EventsManagerHeaderProps {
  combinedCustomEvents: CombinedCustomEvents;
  showArchived: boolean;
  setShowArchived: (showArchived: boolean) => void;
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
}

export const EventsManagerHeader = ({
  combinedCustomEvents,
  showArchived,
  setShowArchived,
  searchInput,
  setSearchInput,
}: EventsManagerHeaderProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const isDataLoading = useIsFetching({
    queryKey: [{ scope: "combinedCustomEvents" }],
  });
  const canCreateCustomEvent = useCanCreateCustomEvent();

  const numArchived = combinedCustomEvents.filter((event) => {
    if ("id" in event) {
      return event.archivedAtTs;
    }
    return false;
  }).length;

  const onCreateEventClick = () => {
    dispatch(
      openDrawer({
        type: "customEvents",
        isCreate: true,
      })
    );
  };

  return (
    <Grid item container direction={"column"} columnSpacing={1} rowSpacing={3}>
      <Grid
        item
        xs
        container
        direction={"row"}
        justifyContent={"space-between"}
        flexWrap={"nowrap"}
        alignItems={"center"}
      >
        <Grid item container direction={"column"} rowSpacing={2}>
          <Grid item>
            <IgTypography
              variant={"h1"}
              sx={{
                fontWeight: 600,
              }}
            >
              Events Manager
            </IgTypography>
          </Grid>
          <Grid item>
            <IgTypography
              sx={{ color: theme.palette.text.secondary }}
              variant={"h3"}
            >
              Manage the custom visitor behaviors tracked on your site -- from
              clicks to page visits, and more.{" "}
            </IgTypography>
          </Grid>
        </Grid>
        <Grid item container xs={4} justifyContent={"flex-end"}>
          <ScopedComponentWithDisable
            customDisabledTooltipMessage={UpgradePlanText}
            isEnabled={canCreateCustomEvent}
            debugId={"create-custom-event"}
          >
            <IgButton variant="contained" onClick={onCreateEventClick}>
              Create Custom Event
            </IgButton>
          </ScopedComponentWithDisable>
        </Grid>
      </Grid>
      <Grid container item direction={"row"} alignItems={"center"}>
        <Grid item xs>
          <IgTextField
            fullWidth={true}
            sx={{
              backgroundColor: "white",
            }}
            placeholder={"Filter by event name"}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              endAdornment: <Search size={16} />,
            }}
          />
        </Grid>
        <IgTooltip
          title={
            "No longer needed custom events that won’t be available for tracking when setting up your tests."
          }
          maxWidth={"165px"}
        >
          <Grid
            item
            container
            xs="auto"
            alignItems={"center"}
            wrap={"nowrap"}
            sx={{
              borderRadius: "5px",
              paddingLeft: "5px",
              paddingRight: "15px",
              height: "37.125px",
              marginLeft: "20px",
              marginRight: "10px",
              "&: hover": {
                cursor: "pointer",
              },
            }}
            onClick={() => setShowArchived(!showArchived)}
          >
            <IgCheckbox checked={showArchived} />
            <IgTypography>Show Archived ({numArchived})</IgTypography>
          </Grid>
        </IgTooltip>
        <Grid item xs="auto" container justifyContent={"flex-end"}>
          <IgTooltip
            title={
              isDataLoading > 0
                ? "Please wait for the data to return before refreshing"
                : "Refresh Data"
            }
          >
            <IgIconButton
              disabled={isDataLoading > 0}
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: combinedCustomEventsKeys.all,
                });
              }}
            >
              <RefreshCcw size={20} />
            </IgIconButton>
          </IgTooltip>
        </Grid>
      </Grid>
    </Grid>
  );
};
